import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import HotelOutlinedIcon from "@mui/icons-material/HotelOutlined";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {
    defineServerTableToolbarItem,
    useServerTableToolbarItemContext
} from "../../common/serverTable";
import {serverTableTextControlSx} from "../../common/serverTable/constants";

export type ProfileStayPeriod = 'ALL' | 'STAYING' | 'CHECKING_IN' | 'CHECKING_OUT';

export const PROFILE_STAY_PERIOD_LABELS: Record<ProfileStayPeriod, string> = {
    ALL: 'All profiles',
    STAYING: 'In-House',
    CHECKING_IN: 'Checking in',
    CHECKING_OUT: 'Checking out'
};

interface Props {
    value: ProfileStayPeriod
    onChange: (value: ProfileStayPeriod) => void
}

/**
 * Scopes the list to a stay window. It cannot join the generic filter panel because
 * each period asks the server a differently shaped question, so it stays page-owned -
 * but registering it keeps it on the toolbar's left on desktop and lets it collapse
 * into More on a phone instead of holding a row open on its own.
 */
const ProfileStayPeriodFilterComponent = ({value, onChange}: Props) => {
    const {t} = useTranslation();
    const {presentation, menuAnchor, closeMenu} = useServerTableToolbarItemContext();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);

    // Collapsed, the trigger unmounts with the menu that held it, so the period list
    // hangs off the More button the same way the managed panels do.
    const openPeriods = (fallbackAnchor: HTMLElement) => {
        closeMenu();
        setAnchor(menuAnchor ?? fallbackAnchor);
    };

    return (
        <>
            {presentation === 'menu' ? (
                <MenuItem onClick={event => openPeriods(event.currentTarget)}>
                    <HotelOutlinedIcon fontSize="small" sx={{mr: 1, color: 'primary.main'}}/>
                    {t(PROFILE_STAY_PERIOD_LABELS[value])}
                </MenuItem>
            ) : (
                <Button
                    size="small"
                    variant="text"
                    color="secondary"
                    endIcon={<ArrowDropDownIcon fontSize="small"/>}
                    aria-haspopup="menu"
                    aria-expanded={!!anchor}
                    onClick={event => openPeriods(event.currentTarget)}
                    sx={serverTableTextControlSx}
                >
                    {t(PROFILE_STAY_PERIOD_LABELS[value])}
                </Button>
            )}

            <Menu
                anchorEl={anchor}
                open={!!anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                transformOrigin={{vertical: 'top', horizontal: 'left'}}
            >
                {(Object.keys(PROFILE_STAY_PERIOD_LABELS) as ProfileStayPeriod[]).map(period => (
                    <MenuItem
                        key={period}
                        selected={period === value}
                        onClick={() => {
                            onChange(period);
                            setAnchor(null);
                        }}
                    >
                        {t(PROFILE_STAY_PERIOD_LABELS[period])}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export const ProfileStayPeriodFilter = defineServerTableToolbarItem(
    ProfileStayPeriodFilterComponent,
    {align: 'start'}
);
